import React, { useState, useRef, useEffect } from "react";
import Calendar from "react-calendar";
import styled from "styled-components";
import dayjs from "dayjs";
import { Theme } from "../atoms/theme";
import { Button } from "@tyrannosaurustech/ui";

const DATE_PATTERN = /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12]\d|3[01])\/(19|20)\d{2}$/;

interface DateOfServicePickerProps {
  fromValue: string;
  toValue: string;
  notesValue?: string;
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
  onNotesChange?: (value: string) => void;
  errors?: { from?: string; to?: string };
  disabled?: boolean;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const DateRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 1rem;
`;

const DateFieldWrapper = styled.div`
  position: relative;
  flex: 1;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.25rem;
  color: ${Theme.colors.neutrals_100};
`;

const InputWrapper = styled.div<{ hasError?: boolean }>`
  display: flex;
  align-items: center;
  border: 1px solid
    ${(props) => (props.hasError ? "#e03131" : Theme.colors.neutrals_400)};
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: #fff;

  &:focus-within {
    border-color: ${Theme.colors.primaries_000};
    box-shadow: 0 0 0 2px ${Theme.colors.primaries_000}22;
  }
`;

const StyledInput = styled.input`
  border: none;
  outline: none;
  flex: 1;
  font-size: 0.875rem;
  color: ${Theme.colors.neutrals_100};
  background: transparent;

  &::placeholder {
    color: ${Theme.colors.neutrals_300};
  }
`;

const CalendarIcon = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  color: ${Theme.colors.neutrals_200};

  &:hover {
    color: ${Theme.colors.primaries_000};
  }
`;

const CalendarDropdown = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  z-index: 100;
  background: #fff;
  border-radius: 0.5rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  border: 1px solid ${Theme.colors.neutrals_400};
`;

const CustomCalendar = styled(Calendar)`
  border-width: 0px;
`;

const ErrorText = styled.span`
  font-size: 0.75rem;
  color: #e03131;
  margin-top: 0.25rem;
  display: block;
`;

const NotesCheckboxWrapper = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  color: ${Theme.colors.neutrals_100};
  cursor: pointer;
  margin-top: 0.25rem;
`;

const NotesTextArea = styled.textarea`
  width: 100%;
  min-height: 80px;
  border: 1px solid ${Theme.colors.neutrals_400};
  border-radius: 0.5rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  font-family: inherit;
  resize: vertical;
  outline: none;

  &:focus {
    border-color: ${Theme.colors.primaries_000};
    box-shadow: 0 0 0 2px ${Theme.colors.primaries_000}22;
  }

  &::placeholder {
    color: ${Theme.colors.neutrals_300};
  }
`;

const CalendarSvg = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

function useClickOutside(
  ref: React.RefObject<HTMLElement | null>,
  onClickOutside: () => void
) {
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        onClickOutside();
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [ref, onClickOutside]);
}

const parseDate = (str: string): Date | null => {
  if (!str || !DATE_PATTERN.test(str)) return null;
  const [month, day, year] = str.split("/").map(Number);
  const d = new Date(year, month - 1, day);
  if (isNaN(d.getTime())) return null;
  return d;
};

const formatDate = (d: Date): string => {
  return dayjs(d).format("MM/DD/YYYY");
};

const DateField = ({
  label,
  value,
  onChange,
  error,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (val: string) => void;
  error?: string;
  disabled?: boolean;
}) => {
  const [showCalendar, setShowCalendar] = useState(false);
  const calendarRef = useRef<HTMLDivElement>(null);
  const today = new Date();

  useClickOutside(calendarRef, () => setShowCalendar(false));

  const handleCalendarChange = (date: Date) => {
    onChange(formatDate(date));
    setShowCalendar(false);
  };

  const calendarValue = parseDate(value);

  return (
    <DateFieldWrapper ref={calendarRef}>
      <Label>
        {label} <span style={{ color: "#e03131" }}>*</span>
      </Label>
      <InputWrapper hasError={!!error}>
        <StyledInput
          type="text"
          placeholder="MM/DD/YYYY"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
        />
        <CalendarIcon
          type="button"
          onClick={() => !disabled && setShowCalendar(!showCalendar)}
          tabIndex={-1}
        >
          <CalendarSvg />
        </CalendarIcon>
      </InputWrapper>
      {error && <ErrorText>{error}</ErrorText>}
      {showCalendar && (
        <CalendarDropdown>
          <CustomCalendar
            onChange={handleCalendarChange as any}
            value={calendarValue || undefined}
            maxDate={today}
          />
        </CalendarDropdown>
      )}
    </DateFieldWrapper>
  );
};

export const DateOfServicePicker: React.FC<DateOfServicePickerProps> = ({
  fromValue,
  toValue,
  notesValue,
  onFromChange,
  onToChange,
  onNotesChange,
  errors,
  disabled,
}) => {
  const [showNotes, setShowNotes] = useState(!!notesValue);

  const handleTodayClick = () => {
    onToChange(formatDate(new Date()));
  };

  return (
    <Wrapper>
      <DateRow>
        <DateField
          label="Date of Service From"
          value={fromValue}
          onChange={onFromChange}
          error={errors?.from}
          disabled={disabled}
        />
        <DateField
          label="Date of Service To"
          value={toValue}
          onChange={onToChange}
          error={errors?.to}
          disabled={disabled}
        />
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            paddingTop: "1.25rem",
            minWidth: "5rem",
          }}
        >
          <Button
            type="button"
            onClick={handleTodayClick}
            label="TODAY"
            base="secondary"
            disabled={disabled}
          />
        </div>
      </DateRow>
      {onNotesChange && (
        <>
          <NotesCheckboxWrapper>
            <input
              type="checkbox"
              checked={showNotes}
              onChange={(e) => {
                setShowNotes(e.target.checked);
                if (!e.target.checked && onNotesChange) {
                  onNotesChange("");
                }
              }}
              disabled={disabled}
            />
            Do you have any additional notes for the DOS? (ex: Dentist only.)
          </NotesCheckboxWrapper>
          {showNotes && (
            <div>
              <Label>Notes</Label>
              <NotesTextArea
                placeholder="Notes"
                value={notesValue || ""}
                onChange={(e) => onNotesChange(e.target.value)}
                disabled={disabled}
              />
            </div>
          )}
        </>
      )}
    </Wrapper>
  );
};

// Validation helper - use in form components
export const validateDateOfService = (
  fromStr: string,
  toStr: string
): { from?: string; to?: string } => {
  const errors: { from?: string; to?: string } = {};
  const today = new Date();
  today.setHours(23, 59, 59, 999);

  if (!fromStr) {
    errors.from = "Date of Service From is required.";
  } else if (!DATE_PATTERN.test(fromStr)) {
    errors.from = "Please enter a valid date (MM/DD/YYYY).";
  } else {
    const fromDate = parseDate(fromStr);
    if (fromDate && fromDate > today) {
      errors.from = "Date of Service cannot be in the future.";
    }
  }

  if (!toStr) {
    errors.to = "Date of Service To is required.";
  } else if (!DATE_PATTERN.test(toStr)) {
    errors.to = "Please enter a valid date (MM/DD/YYYY).";
  } else {
    const toDate = parseDate(toStr);
    if (toDate && toDate > today) {
      errors.to = "Date of Service cannot be in the future.";
    }
  }

  const fromDate = parseDate(fromStr);
  const toDate = parseDate(toStr);
  if (fromDate && toDate && fromDate > toDate) {
    errors.from = "From date cannot be after To date.";
  }

  return errors;
};

export default DateOfServicePicker;
